import React, { createContext, useContext, useState, useCallback } from 'react';
import './Toast.css';

const ToastContext = createContext();

const DEFAULT_ERROR_MESSAGE = 'Something went wrong';

function _normalizeToastInput(input, fallback = DEFAULT_ERROR_MESSAGE) {
  if (!input) return { message: fallback, details: null };
  if (typeof input === 'string') return { message: input, details: null };
  if (input instanceof Error) {
    return { message: input.message || fallback, details: input.stack || null };
  }

  // Common HTTP error shapes (axios, fetch wrappers, appwrite, etc.)
  if (input.response) {
    const data = input.response.data;
    const message = (data && (data.message || data.error)) || input.message || fallback;
    const details = data ? JSON.stringify(data, null, 2) : (input.stack || null);
    return { message, details };
  }

  // If it's an object with message property
  if (typeof input === 'object') {
    const message = input.message || JSON.stringify(input);
    const details = JSON.stringify(input, null, 2);
    return { message, details };
  }

  return { message: String(input), details: null };
}

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);
  const [toastKey, setToastKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const showToast = useCallback((messageOrObj, type = 'success') => {
    const { message, details } = _normalizeToastInput(messageOrObj, messageOrObj || (type === 'error' ? DEFAULT_ERROR_MESSAGE : ''));

    // Reset expanded state for new toast
    setExpanded(false);

    // Clear existing toast first to prevent overlap
    setToast(null);
    
    // Small delay to ensure clean animation
    setTimeout(() => {
      setToast({ message, type, details });
      setToastKey(prev => prev + 1); // Force re-render with new animation
      
      // Auto-hide: longer timeout when details are present
      const duration = details ? 8000 : 3000;
      setTimeout(() => {
        setToast(null);
        setExpanded(false);
      }, duration);
    }, 50);
  }, []);

  const showSuccess = useCallback((message) => {
    showToast(message, 'success');
  }, [showToast]);

  const showError = useCallback((errorOrMessage, fallbackMessage) => {
    // Allow calling showError(err) or showError(err, 'fallback')
    const input = (typeof fallbackMessage === 'string') ? (errorOrMessage || fallbackMessage) : errorOrMessage;
    showToast(input, 'error');
  }, [showToast]);

  const showInfo = useCallback((message) => {
    showToast(message, 'info');
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo }}>
      {children}
      {toast && (
        <div key={toastKey} className={`toast-notification toast-${toast.type}`}>
          <div className="toast-message">{toast.message}</div>

          {toast.details && (
            <div className="toast-controls">
              <button className="toast-toggle" onClick={() => setExpanded(prev => !prev)}>{expanded ? 'Hide details' : 'Show details'}</button>
            </div>
          )}

          {expanded && toast.details && (
            <pre className="toast-details" aria-live="polite">{toast.details}</pre>
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
};
